import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { z } from "zod";
import { formFields } from "./edit_card";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../L1StaticData/edit-form-conf";
import { useTranslation } from "react-i18next";

const filterL1TimeSeriesSchema = z.object({
  l1_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  currency_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

const filterL1TimeSeriesFields = filterL1TimeSeriesSchema.keyof().enum;

export type FilterL1TimeSeriesSchema = z.infer<typeof filterL1TimeSeriesSchema>;

const assetDepFields: readonly DepField<FilterL1TimeSeriesSchema>[] = [
  {
    name: filterL1TimeSeriesFields.l1_static_datum_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterL1TimeSeriesFields.isin_contains,
    key: "isin",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterL1TimeSeries() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.L1_TIME_SERIES;

  const fields: Field[] = [
    {
      name: filterL1TimeSeriesFields.l1_static_datum_name_contains,
      label: t(`${tableName}.searchbox.l1_static_datum_name_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_STATIC_DATA}`,
      id_column: formFields.l1_static_datum_id,
      column_name: "name",
      col_span: 2,
      filters: makeFiltersBuilder<FilterL1TimeSeriesSchema>(
        assetDepFields,
        filterL1TimeSeriesFields.l1_static_datum_name_contains,
      ),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterL1TimeSeriesFields.asset_class_1_equals,
      assetSubclassFieldName: filterL1TimeSeriesFields.asset_class_2_equals,
      assetClassLabel: t(`${Tables.L1_TIME_SERIES}.filter.asset_class_1`),
      assetSubclassLabel: t(`${Tables.L1_TIME_SERIES}.filter.asset_class_2`),
      client: Clients.L1,
      staticDataTableName: Tables.L1_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterL1TimeSeriesFields.isin_contains,
      label: t(`${tableName}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_STATIC_DATA}`,
      id_column: formFields.l1_static_datum_id,
      column_name: "isin",
      filters: makeFiltersBuilder<FilterL1TimeSeriesSchema>(
        assetDepFields,
        filterL1TimeSeriesFields.isin_contains,
      ),
    },
    {
      name: filterL1TimeSeriesFields.currency_contains,
      label: t(`${tableName}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterL1TimeSeriesFields.date_from },
      to: { name: filterL1TimeSeriesFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterL1TimeSeriesFields.create_from },
      to: { name: filterL1TimeSeriesFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      schema={filterL1TimeSeriesSchema}
      client={Clients.L1}
      tableName={Tables.L1_TIME_SERIES}
      fields={fields}
      columns={2}
    />
  );
}
